/**
 * Collections API Module
 * Handles all collection-related API operations
 */

import { api } from '../core/api.client';
import { API_ENDPOINTS } from '../core/api.config';
import {
  ApiClientResponse,
  QueryParams,
  RequestOptions,
  BaseDocument,
  LandingConfig,
  Media,
} from '../core/api.types';

/**
 * Generic collection API class
 */
export class CollectionApi<T extends BaseDocument = BaseDocument> {
  constructor(protected collectionSlug: string) {}

  /**
   * Get list of documents with pagination and filters
   */
  async list(params?: QueryParams, options?: RequestOptions): Promise<ApiClientResponse<T[]>> {
    return api.get<T[]>(this.collectionSlug, {
      ...options,
      params: {
        ...params,
        limit: params?.limit || 10,
        page: params?.page || 1,
      },
    });
  }

  /**
   * Get single document by ID
   */
  async getById(id: string, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.get<T>(`${this.collectionSlug}/${id}`, options);
  }

  /**
   * Get single document by query
   */
  async findOne(params: QueryParams, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    const response = await this.list({ ...params, limit: 1 }, options);
    
    if (response.success && response.data && response.data.length > 0) {
      return {
        success: true,
        data: response.data[0],
      };
    }

    return {
      success: false,
      error: {
        status: 404,
        message: 'Document not found',
      },
    };
  }

  /**
   * Create new document
   */
  async create(data: Partial<T>, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.post<T>(this.collectionSlug, data, options);
  }

  /**
   * Update document partially (PATCH)
   */
  async update(id: string, data: Partial<T>, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.patch<T>(`${this.collectionSlug}/${id}`, data, options);
  }

  /**
   * Replace document completely (PUT)
   */
  async replace(id: string, data: Partial<T>, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.put<T>(`${this.collectionSlug}/${id}`, data, options);
  }

  /**
   * Delete document
   */
  async delete(id: string, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.delete<T>(`${this.collectionSlug}/${id}`, options);
  }

  /**
   * Bulk create documents
   */
  async bulkCreate(documents: Partial<T>[], options?: RequestOptions): Promise<ApiClientResponse<T[]>> {
    const results = await Promise.all(
      documents.map(doc => this.create(doc, options))
    );

    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);

    if (failed.length > 0) {
      return {
        success: false,
        data: successful.map(r => r.data!),
        error: {
          status: 207, // Multi-status
          message: `${failed.length} documents failed to create`,
          errors: failed.map(r => ({ message: r.error?.message || 'Unknown error' })),
        },
      };
    }

    return {
      success: true,
      data: successful.map(r => r.data!),
    };
  }

  /**
   * Bulk delete documents
   */
  async bulkDelete(ids: string[], options?: RequestOptions): Promise<ApiClientResponse<void>> {
    const results = await Promise.all(
      ids.map(id => this.delete(id, options))
    );

    const failed = results.filter(r => !r.success);

    if (failed.length > 0) {
      return {
        success: false,
        error: {
          status: 207,
          message: `${failed.length} documents failed to delete`,
          errors: failed.map(r => ({ message: r.error?.message || 'Unknown error' })),
        },
      };
    }

    return { success: true };
  }
}

/**
 * Landing Config API
 */
export class LandingConfigApi extends CollectionApi<LandingConfig> {
  constructor() {
    super(API_ENDPOINTS.COLLECTIONS.LANDING_CONFIG);
  }

  /**
   * Get config value by key
   */
  async getByKey(key: string, options?: RequestOptions): Promise<ApiClientResponse<any>> {
    const response = await this.findOne(
      {
        where: {
          key: { equals: key },
        },
      },
      options
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.value,
      };
    }

    return response;
  }

  /**
   * Set config value by key
   */
  async setByKey(key: string, value: any, options?: RequestOptions): Promise<ApiClientResponse<LandingConfig>> {
    // First try to find existing config
    const existing = await this.findOne({
      where: { key: { equals: key } },
    });

    if (existing.success && existing.data) {
      // Update existing
      return this.update(existing.data.id, { value }, options);
    } else {
      // Create new
      return this.create({ key, value }, options);
    }
  }

  /**
   * Get multiple config values by keys
   */
  async getMultiple(keys: string[], options?: RequestOptions): Promise<ApiClientResponse<Record<string, any>>> {
    const response = await this.list(
      {
        where: {
          key: { in: keys },
        },
        limit: keys.length,
      },
      options
    );

    if (response.success && response.data) {
      const configMap: Record<string, any> = {};
      response.data.forEach(config => {
        configMap[config.key] = config.value;
      });
      return {
        success: true,
        data: configMap,
      };
    }

    return {
      success: false,
      error: response.error,
    };
  }
}

/**
 * Media API
 */
export class MediaApi extends CollectionApi<Media> {
  constructor() {
    super(API_ENDPOINTS.COLLECTIONS.MEDIA);
  }

  /**
   * Upload media file
   */
  async upload(
    file: File | Blob,
    data?: Partial<Media>,
    options?: RequestOptions
  ): Promise<ApiClientResponse<Media>> {
    return api.upload<Media>(this.collectionSlug, file, data as any, options);
  }

  /**
   * Get media by filename
   */
  async getByFilename(filename: string, options?: RequestOptions): Promise<ApiClientResponse<Media>> {
    return this.findOne(
      {
        where: {
          filename: { equals: filename },
        },
      },
      options
    );
  }

  /**
   * Get media by mime type
   */
  async getByMimeType(mimeType: string, params?: QueryParams, options?: RequestOptions): Promise<ApiClientResponse<Media[]>> {
    return this.list(
      {
        ...params,
        where: {
          ...params?.where,
          mimeType: { equals: mimeType },
        },
      },
      options
    );
  }

  /**
   * Get images only
   */
  async getImages(params?: QueryParams, options?: RequestOptions): Promise<ApiClientResponse<Media[]>> {
    return this.list(
      {
        ...params,
        where: {
          ...params?.where,
          mimeType: { like: 'image/%' },
        },
      },
      options
    );
  }
}

/**
 * Create custom collection API instance
 */
export function createCollectionApi<T extends BaseDocument = BaseDocument>(
  collectionSlug: string
): CollectionApi<T> {
  return new CollectionApi<T>(collectionSlug);
}

// Export pre-configured instances
export const landingConfigApi = new LandingConfigApi();
export const mediaApi = new MediaApi();

// Export generic collection APIs
export const collections = {
  landing: landingConfigApi,
  media: mediaApi,
  posts: createCollectionApi(API_ENDPOINTS.COLLECTIONS.POSTS),
  pages: createCollectionApi(API_ENDPOINTS.COLLECTIONS.PAGES),
};
